import { useEffect, useRef } from 'react'
import { useToast } from '@orbind/ui'
import { isDesktop } from '../lib/platform.ts'

/**
 * Aviso de NOVA VERSAO no Orbind Desktop (2026-08-25).
 *
 * O shell do desktop carrega o app web direto do servidor, entao abrir o app
 * ja entrega a versao mais recente. Este hook cobre o caso restante: a janela
 * que fica ABERTA enquanto um deploy acontece. Ele compara periodicamente o
 * bundle do `index.html` servido com o que esta rodando; quando muda, oferece
 * um toast "Atualizar" que recarrega a janela.
 *
 * So roda no desktop: no navegador o F5 do usuario ja resolve, e o toast
 * viraria ruido.
 */

const INTERVALO_MS = 5 * 60 * 1000
/** O toast se auto-fecha em segundos; quem nao viu merece ser relembrado —
 *  mas nao a cada alt-tab. */
const REAVISO_MS = 15 * 60 * 1000

/**
 * ATUALIZACAO SILENCIOSA (SPEC-20260916-2111, pedido 2026-09-16 21:11/21:24:
 * "mecanismo de verificacao de atualizacao… agora a solucao fica muito tempo
 * rodando" e "fazer a atualizacao de forma silenciosa"). Com o Orbind na
 * bandeja ninguem clica em "Atualizar", entao o toast saiu de cena: uma versao
 * nova recarrega a janela SOZINHA assim que da para fazer isso sem atrapalhar —
 * janela escondida/minimizada, ou visivel mas parada ha um tempo e sem campo
 * ou editor em foco. Quem esta digitando nunca e interrompido; a proxima
 * checagem pega.
 */
export const OCIOSA_APOS_MS = 2 * 60 * 1000

export function podeRecarregarSemAtrapalhar(entrada: {
  visibilidade: DocumentVisibilityState
  /** Tag/estado do elemento em foco. */
  focoEmEdicao: boolean
  /** Quanto tempo faz que a pessoa mexeu no mouse/teclado (ms). */
  paradaHa: number
}): boolean {
  if (entrada.focoEmEdicao) return false
  if (entrada.visibilidade === 'hidden') return true
  return entrada.paradaHa >= OCIOSA_APOS_MS
}

function focoEmEdicao(): boolean {
  const ativo = document.activeElement
  return Boolean(
    ativo && (ativo.tagName === 'INPUT' || ativo.tagName === 'TEXTAREA' || ativo.tagName === 'SELECT' || (ativo as HTMLElement).isContentEditable),
  )
}

/** Assinatura da versao: o nome hasheado do bundle principal no index.html. */
export function assinaturaDe(html: string): string | null {
  return html.match(/\/assets\/index-[\w-]+\.js/)?.[0] ?? null
}

/** O bundle em EXECUCAO nesta janela (null em dev, que nao tem hash). */
export function assinaturaRodando(): string | null {
  return (
    [...document.scripts]
      .map((s) => s.getAttribute('src') ?? '')
      .map((src) => assinaturaDe(src))
      .find((a) => a !== null) ?? null
  )
}

export async function assinaturaNoServidor(): Promise<string | null> {
  try {
    const res = await fetch('/', { cache: 'no-store' })
    if (!res.ok) return null
    return assinaturaDe(await res.text())
  } catch {
    return null // offline ou servidor fora: tenta de novo no proximo ciclo
  }
}

export function useAvisoDeNovaVersao() {
  const { push } = useToast()
  // Ultima vez que a pessoa mexeu (mouse/teclado): decide se a recarga
  // silenciosa atrapalharia alguem.
  const mexeuEm = useRef(Date.now())
  // Versao nova ja vista mas ainda nao aplicada (pessoa ativa): reavalia a
  // cada minuto sem bater no servidor de novo.
  const pendente = useRef(false)
  const avisadoEm = useRef(0)

  useEffect(() => {
    if (!isDesktop()) return
    // Baseline = o bundle que esta rodando agora (em dev nao ha hash: sai).
    const rodando = assinaturaRodando()
    if (!rodando) return

    let cancelado = false
    const tentarAplicar = () => {
      if (!pendente.current) return
      const pode = podeRecarregarSemAtrapalhar({
        visibilidade: document.visibilityState,
        focoEmEdicao: focoEmEdicao(),
        paradaHa: Date.now() - mexeuEm.current,
      })
      if (pode) window.location.reload()
      // pessoa ativa ha muito tempo: um aviso discreto, no maximo a cada 15 min,
      // para quem nunca para saber que ha versao nova (silencio nao e segredo)
      else if (Date.now() - avisadoEm.current >= REAVISO_MS) {
        avisadoEm.current = Date.now()
        push({
          tone: 'info',
          message: 'Nova versão do Orbind pronta — será aplicada quando você parar de mexer.',
          action: { label: 'Aplicar agora', onClick: () => window.location.reload() },
        })
      }
    }
    const verificar = async () => {
      if (pendente.current) {
        tentarAplicar()
        return
      }
      const noServidor = await assinaturaNoServidor()
      if (cancelado || !noServidor) return
      if (noServidor === rodando) return
      pendente.current = true
      tentarAplicar()
    }

    const registrarAtividade = () => {
      mexeuEm.current = Date.now()
    }
    window.addEventListener('pointerdown', registrarAtividade, { passive: true })
    window.addEventListener('pointermove', registrarAtividade, { passive: true })
    window.addEventListener('keydown', registrarAtividade, { passive: true })

    const timer = setInterval(verificar, INTERVALO_MS)
    // com versao pendente, reavalia a cada minuto (a pessoa pode ter parado)
    const timerPendente = setInterval(tentarAplicar, 60 * 1000)
    // Voltar o foco pra janela e o momento natural de conferir.
    window.addEventListener('focus', verificar)
    // Esconder na bandeja tambem: a janela some e, se houver versao nova,
    // recarrega sem ninguem esperar
    const aoMudarVisibilidade = () => {
      if (document.visibilityState === 'hidden') void verificar()
    }
    document.addEventListener('visibilitychange', aoMudarVisibilidade)
    return () => {
      cancelado = true
      clearInterval(timer)
      clearInterval(timerPendente)
      window.removeEventListener('focus', verificar)
      window.removeEventListener('pointerdown', registrarAtividade)
      window.removeEventListener('pointermove', registrarAtividade)
      window.removeEventListener('keydown', registrarAtividade)
      document.removeEventListener('visibilitychange', aoMudarVisibilidade)
    }
  }, [push])
}
